import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  BookOpen, 
  Clock, 
  Star,
  Filter,
  TrendingUp,
  Users,
  Play,
  Bookmark
} from 'lucide-react';

export function ExploreTab() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Subjects', count: 156 },
    { id: 'philosophy', label: 'Philosophy', count: 45 },
    { id: 'science', label: 'Natural Sciences', count: 38 },
    { id: 'mathematics', label: 'Mathematics', count: 28 },
    { id: 'history', label: 'History', count: 25 },
    { id: 'law', label: 'Law', count: 20 }
  ];

  const featuredCourses = [
    {
      id: 1,
      title: 'Introduction to Stoicism',
      instructor: 'Dr. Marcus Williams',
      category: 'Philosophy',
      difficulty: 'Beginner',
      duration: '4 weeks',
      rating: 4.8,
      enrolled: 1234,
      description: 'Explore the fundamental principles of Stoic philosophy and their practical applications.',
      isFeatured: true
    },
    {
      id: 2,
      title: 'Quantum Mechanics Fundamentals',
      instructor: 'Prof. Lisa Chen',
      category: 'Physics',
      difficulty: 'Intermediate',
      duration: '6 weeks',
      rating: 4.9,
      enrolled: 856,
      description: 'Dive into the fascinating world of quantum mechanics and its implications.',
      isFeatured: true
    },
    {
      id: 3,
      title: 'Constitutional Law Basics',
      instructor: 'Judge Robert Taylor',
      category: 'Law',
      difficulty: 'Intermediate',
      duration: '5 weeks',
      rating: 4.7,
      enrolled: 642,
      description: 'Understanding the foundations of constitutional law and legal interpretation.',
      isFeatured: false
    }
  ];

  const trendingTopics = [
    { name: 'Artificial Intelligence Ethics', courses: 12, trend: '+15%' },
    { name: 'Climate Science', courses: 8, trend: '+22%' },
    { name: 'Digital Philosophy', courses: 6, trend: '+8%' },
    { name: 'Bioethics', courses: 10, trend: '+12%' }
  ];

  const filteredCourses = featuredCourses.filter(course => 
    (selectedCategory === 'all' || course.category.toLowerCase().includes(selectedCategory)) &&
    (searchTerm === '' || course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
     course.instructor.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-black">Explore</h1>
          <p className="text-gray-600">Discover new courses and expand your knowledge</p>
        </div>
        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
          156 Courses Available
        </Badge>
      </div>

      {/* Search and Filters */}
      <Card className="bg-white border border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search courses, instructors, or topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                  className="whitespace-nowrap"
                >
                  {category.label} ({category.count})
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trending Topics */}
      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle className="text-black flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Trending Topics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {trendingTopics.map((topic, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-black mb-1">{topic.name}</h4>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{topic.courses} courses</span>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    {topic.trend}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Course Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredCourses.map((course) => (
          <Card key={course.id} className="bg-white border border-gray-200">
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Course Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-black">{course.title}</h3>
                      {course.isFeatured && (
                        <Badge className="bg-yellow-100 text-yellow-700">Featured</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">by {course.instructor}</p>
                    <p className="text-sm text-gray-500">{course.description}</p>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Bookmark className="h-4 w-4" />
                  </Button>
                </div>

                {/* Course Stats */}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span>{course.rating}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{course.enrolled.toLocaleString()} enrolled</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{course.duration}</span>
                  </div>
                </div>

                {/* Course Meta */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {course.category}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {course.difficulty}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <BookOpen className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                    <Button size="sm">
                      <Play className="h-4 w-4 mr-2" />
                      Enroll
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Browse More */}
      <Card className="bg-white border border-gray-200">
        <CardContent className="p-6 text-center">
          <h3 className="text-lg font-semibold text-black mb-2">Looking for something specific?</h3>
          <p className="text-gray-600 mb-4">Browse our complete catalog or request a new course</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Advanced Filters
            </Button>
            <Button>
              <BookOpen className="h-4 w-4 mr-2" />
              Browse All Courses
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
